import { useNavigate } from 'react-router-dom';

import documentRequestCandidateService from 'store/document-request-candidate/service';
import { DriverDocumentsPage } from 'routes/paths';
import { b64toFile } from 'functions/file/b64toFile';
import styles from './styles.module.scss';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import StepDescription from '../StepDescription';
import ImagePreview, { ImagePreviewStatus } from 'components/ImagePreview';
import {
  ScanStepsEnum,
  initialState,
  nextStepAction,
  previousStepAction,
  reducer,
  setImageStatusAction,
  setImageUloadProgressAction,
} from './reducer';
import { useGetUploadLicenseStatus } from 'features/Driver/LicenseCameraScan/useGetUploadLicenseStatus';
import {
  DocumentProcessStatusFieldsEnum,
  documentProcessErrorsStatus,
  DocumentProcessStatusEnum,
} from 'common/document/enums/document-status.enum';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { useDeviceFeatures } from 'hooks/useDeviceFeatures';
import { useFeatureFlags } from '../../../../contexts/featureFlag/useFeatureFlags';
import { PREDEFINED_ERROR_MESSAGES } from 'components/VideoStream/errorMessages';
import { ESegmentCategory, track } from 'integrations/analytics';
import { DocumentVerificationUpdated } from 'common/document/interfaces/document-verification-updated-message';
import { UploadImageV2 } from 'components/UploadImageV2';
import { Alert, AlertTitle } from 'components/Alert';
import { documentTypeToLabel } from 'functions/documents/documentUtils';
import UploadMultipleImagesV2, {
  ISelectedFile,
} from '../../../../components/UploadMultipleImagesV2';
import ButtonWithArrowIcon from '../../../../components/Buttons/ButtonWithArrowIcon';
import heic2any from 'heic2any';

export interface DriverUploadGenericDocumentPageProps {
  documentRequestId: string;
  documentId: string;
  documentType: DocumentTypeEnum;
  documentTypeEnum?: DocumentTypeEnum;
  useManulaUpload?: boolean;
}

const DriverUploadGenericDocumentPage = (
  props: DriverUploadGenericDocumentPageProps
) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const [mediaStreamTrack] = useState<MediaStreamTrack>();
  const deviceFeatures = useDeviceFeatures(mediaStreamTrack);
  const { featureFlags } = useFeatureFlags();
  const [selectedFiles, setSelectedFiles] = useState<ISelectedFile[]>([]);
  const [uploadingFilesStatus, setUploadingFilesStatus] = useState<
    Map<
      string,
      {
        id: string;
        isLoading?: boolean;
        status?: string;
        error?: string;
      }
    >
  >(new Map());
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const { data: currentDocumentData } =
    documentRequestCandidateService.endpoints.getCandidateDocumentById.useQuery(
      {
        documentId: props.documentId,
      },
      {
        skip: !props.documentId,
      }
    );

  const [getUrlToUploadFile] =
    documentRequestCandidateService.endpoints.getPresignedUrlForUploading.useLazyQuery();

  const [checkPreProcessingStatus] =
    documentRequestCandidateService.endpoints.checkPreProcessingStatus.useLazyQuery();

  const [createBasicUploadRequest] =
    documentRequestCandidateService.endpoints.createBasicUploadRequest.useMutation();

  const [resetDocumentFiles] =
    documentRequestCandidateService.endpoints.resetDocumentFiles.useMutation();

  const uploadDotMedApi = useGetUploadLicenseStatus();

  const onProcessing = () => {
    dispatch(setImageStatusAction(ImagePreviewStatus.PROCESSING));
  };

  const onProgressing = (progress: number) => {
    dispatch(setImageUloadProgressAction(progress));
  };

  const onProcessed = (documentStatus: DocumentVerificationUpdated) => {
    if (documentStatus?.verificationStatus) {
      const status = documentProcessErrorsStatus.includes(
        documentStatus.verificationStatus
      )
        ? ImagePreviewStatus.ERROR
        : ImagePreviewStatus.SUCCESS;
      const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
        documentStatus.verificationStatus
      ];

      if (
        documentStatus?.verificationStatus === DocumentProcessStatusEnum.FAILED
      ) {
        checkPreProcessingStatus({ documentId: props.documentId })
          .unwrap()
          .then((preProcessingStatuses: any) => {
            const preProcessingStatusMap = new Map<string, any>(
              Object.entries(preProcessingStatuses)
            );
            if (
              state.step === ScanStepsEnum.preview &&
              preProcessingStatusMap.has(
                DocumentProcessStatusFieldsEnum.PRE_PROCESSING_STATUS
              )
            ) {
              const documentStatus = preProcessingStatusMap.get(
                DocumentProcessStatusFieldsEnum.PRE_PROCESSING_STATUS
              );
              const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
                documentStatus.verificationStatus
              ];
              dispatch(
                setImageStatusAction(
                  ImagePreviewStatus.SUCCESS,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            } else {
              dispatch(
                setImageStatusAction(
                  status,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            }
          })
          .catch(() => {
            dispatch(
              setImageStatusAction(
                status,
                defaultMessage?.message,
                defaultMessage?.label
              )
            );
          });
      } else {
        dispatch(
          setImageStatusAction(
            status,
            defaultMessage?.message,
            defaultMessage?.label
          )
        );
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (state.step === ScanStepsEnum.preview) {
        let url: any = {};
        try {
          url = await getUrlToUploadFile({
            documentId: props.documentId,
            fileTypes: state?.file?.type ? [state?.file?.type] : ['image/png'],
          }).unwrap();
        } catch (e: any) {
          dispatch(
            setImageStatusAction(ImagePreviewStatus.ERROR, e.message, e.message)
          );
          console.error(e);
        }

        if (url?.file) {
          const file =
            state.file || b64toFile(state.image!, 'image/png', 'LicenseFront');
          uploadDotMedApi.uploadDocument(
            file,
            url.file,
            DocumentProcessStatusFieldsEnum.PRE_PROCESSING_STATUS,
            onProgressing,
            onProcessing,
            onProcessed
          );
          track(ESegmentCategory.IMAGE_UPLOADED, {
            type: props.documentType,
            manualUpload: props.useManulaUpload,
            deviceFeatures,
            validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
            focusQuality: state.focusQuality,
            focusQualityThreshold: state.focusQualityThreshold,
          });
        }
      }

      if (state.step === ScanStepsEnum.endOfTheFlow) {
        navigate(`${DriverDocumentsPage}?from=${props.documentType}`);
      }
    })();
  }, [getUrlToUploadFile, state.step]);

  const onFileInput = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        let data:
          | {
              mainImage: string;
              focusQuality: number;
              focusQualityThreshold: number;
              file?: File;
            }
          | undefined;
        const base64Image = e.target?.result as string;
        // HEIC → PNG convert
        if (
          file.type === 'image/heic' ||
          file.type === 'image/heif' ||
          file.name.toLowerCase().endsWith('.heic') ||
          file.name.toLowerCase().endsWith('.heif')
        ) {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: 'image/png',
            });

            const newFileName = file.name.replace(/\.(heic|heif)$/i, '.png');

            const updatedFile = new File([convertedBlob as Blob], newFileName, {
              type: 'image/png',
            });

            data = {
              mainImage: base64Image,
              focusQuality: 100,
              focusQualityThreshold: -1,
              file: updatedFile,
            };
          } catch (err) {
            console.error('Error HEIC convert:', err);
            return;
          }
        } else {
          data = {
            mainImage: base64Image,
            focusQuality: 100,
            focusQualityThreshold: -1,
            file,
          };
        }
        dispatch(nextStepAction(data));
        track(ESegmentCategory.MANUAL_UPLOAD_IMAGE_SELECTED);
      };
      reader.readAsDataURL(file);
    }
  };

  const onFilesInput = (files: ISelectedFile[]) => {
    if (files?.length) {
      setSelectedFiles((prevState: ISelectedFile[]) => {
        return [...prevState, ...files];
      });
    }
  };

  const onRemoveFile = (id: string) => {
    if (id) {
      setSelectedFiles((prevState: ISelectedFile[]) => {
        return prevState.filter((file) => file.id !== id) as ISelectedFile[];
      });
    }
  };

  const onContinueClick = () => {
    dispatch(nextStepAction());
  };

  const onRetakeClick = () => {
    track(ESegmentCategory.RETAKE_PHOTO);
    dispatch(previousStepAction());
  };

  const handleUploadFiles = useCallback(async () => {
    /*
    we are using this flow like this:
    1) upload files to the server for keys: "file{n}"
    2) merge all these files to the one PDF document and upload fto the "file"
    See document in DB. you will find something like this:
    "files": {
      "file": {
        "fileName": "679f914538f1fca48254d061-RETURN_TO_WORK-file",
        .....
      },
      "file0": {
        "fileName": "679f914538f1fca48254d061-RETURN_TO_WORK-file0",
        ....
      }
    },
     */
    setIsUploadingFiles(true);
    if (selectedFiles.length && currentDocumentData?.document?.id) {
      await resetDocumentFiles({
        documentId: currentDocumentData.document.id,
      }).unwrap();

      let index = 0;
      for (const selectedFile of selectedFiles) {
        const uploadRequest: any = await createBasicUploadRequest({
          documentId: currentDocumentData.document.id,
          fileName: `${currentDocumentData.document.documentRequestId}-${currentDocumentData.document.documentType}-file${index}`,
          fileOriginalName: selectedFile.file.name,
          fileType: selectedFile.file.type,
          candidateId: currentDocumentData.document.candidateId,
        }).unwrap();

        if (uploadRequest?.uploadUrl) {
          try {
            setUploadingFilesStatus((prevState) => {
              const result = new Map([...Array.from(prevState.entries())]);

              result.set(selectedFile.id, {
                id: selectedFile.id,
                isLoading: true,
                status: '',
                error: '',
              });

              return result;
            });
            await uploadDotMedApi.uploadDocument(
              selectedFiles[index].file,
              uploadRequest.uploadUrl,
              DocumentProcessStatusFieldsEnum.PRE_PROCESSING_STATUS,
              onProgressing,
              onProcessing,
              onProcessed
            );
            setUploadingFilesStatus((prevState) => {
              const result = new Map([...Array.from(prevState.entries())]);

              result.set(selectedFile.id, {
                id: selectedFile.id,
                isLoading: false,
                status: 'success',
                error: '',
              });

              return result;
            });
            track(ESegmentCategory.IMAGE_UPLOADED, {
              type: props.documentType,
              manualUpload: props.useManulaUpload,
              deviceFeatures,
              validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
              focusQuality: state.focusQuality,
              focusQualityThreshold: state.focusQualityThreshold,
            });
          } catch (error) {
            setUploadingFilesStatus((prevState) => {
              const result = new Map([...Array.from(prevState.entries())]);

              result.set(selectedFile.id, {
                id: selectedFile.id,
                isLoading: false,
                status: 'error',
                error: 'File upload failed. Please try again later.',
              });

              return result;
            });
          }
        } else {
          setIsUploadingFiles(false);
        }
        index++;
      }
    }
    setIsUploadingFiles(false);
    navigate(`${DriverDocumentsPage}?from=${props.documentType}`);
  }, [
    selectedFiles,
    currentDocumentData,
    props.documentType,
    props.useManulaUpload,
  ]);

  const supportPdf = useMemo(() => {
    return currentDocumentData?.document?.acceptedFileTypes?.includes('PDF');
  }, [currentDocumentData]);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {(state.step === ScanStepsEnum.capture ||
          state.step === ScanStepsEnum.preview) && (
          <StepDescription
            title={`Upload ${documentTypeToLabel(
              props.documentTypeEnum!
            )} document`}
          />
        )}

        {state.step !== ScanStepsEnum.preview &&
          (!currentDocumentData?.document?.isMultipleUploadAllowed ? (
            <UploadImageV2
              placeholder="Upload Photo"
              onChange={onFileInput}
              supportPdf={supportPdf}
            />
          ) : (
            <UploadMultipleImagesV2
              placeholder="Upload Photo"
              onChange={onFilesInput}
              onRemoveFile={onRemoveFile}
              selectedFiles={selectedFiles || []}
              uploadStatus={uploadingFilesStatus}
              isUploadingFiles={isUploadingFiles}
            />
          ))}
        <Alert severity="warning">
          <AlertTitle>
            Make sure photo is on a flat well lit surface and all edges are
            visible.
          </AlertTitle>
        </Alert>
        {currentDocumentData?.document?.isMultipleUploadAllowed &&
          !!selectedFiles?.length && (
            <div className={styles.actions}>
              <ButtonWithArrowIcon
                onClick={() => handleUploadFiles()}
                variant="contained"
                color="primary"
                disabled={isUploadingFiles}
              >
                Upload
              </ButtonWithArrowIcon>
            </div>
          )}

        {state.step === ScanStepsEnum.preview && (
          <ImagePreview
            file={state.file}
            hideManualUploadButton={props.useManulaUpload}
            documentType="generic"
            documentTypeEnum={props.documentType}
            documentRequestId={props.documentRequestId}
            documentId={props.documentId}
            source={state.imagePreview || state.image}
            status={state.imageStatus}
            imageUploadProgress={state.imageUploadProgress}
            errorMessage={state.imageErrorMessage}
            errorLabel={state.imageErrorLabel}
            buttons={{
              hideRetake: state.imageStatus === ImagePreviewStatus.SUCCESS,
              onApproveClick:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? onContinueClick
                  : onRetakeClick,
              onRetakeClick: onRetakeClick,
              approveText:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? 'Next'
                  : props.useManulaUpload
                  ? 'Select image'
                  : 'Retake Photo',
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DriverUploadGenericDocumentPage;
